import type { CSSProperties, FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface RevealProps {
    children: ReactNode
    className?: string
    delay?: number
}

type RevealState = 'idle' | 'hidden' | 'shown'

/*
 * Fades content in the first time it scrolls into view. Content renders
 * visible on the server and is only hidden after mount when it sits below
 * the fold, so nothing is ever lost without JavaScript.
 */
export const Reveal: FC<RevealProps> = ({ children, className, delay = 0 }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const [state, setState] = useState<RevealState>('idle')

    useEffect(() => {
        const element = ref.current

        if (!element || typeof IntersectionObserver === 'undefined') return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
        if (element.getBoundingClientRect().top < window.innerHeight * 0.92) return

        setState('hidden')

        const observer = new IntersectionObserver(entries => {
            if (!entries.some(entry => entry.isIntersecting)) return

            setState('shown')
            observer.disconnect()
        }, { rootMargin: '0px 0px -12% 0px' })

        observer.observe(element)

        return () => observer.disconnect()
    }, [])

    const style: CSSProperties | undefined = state === 'shown' && delay ? { animationDelay: `${delay}ms` } : undefined

    return (
        <div
            ref={ref}
            style={style}
            className={cn(
                className,
                state === 'hidden' && 'opacity-0',
                state === 'shown' && 'animate-in fade-in slide-in-from-bottom-6 fill-mode-both duration-700 ease-out'
            )}
        >
            {children}
        </div>
    )
}
